/**
 * RenewContractModal — 1-click contract renewal for a permanent.
 * The admin picks the new period (any duration — monthly, yearly,
 * custom) and PREREC regenerates the PDF using the same tarif grid
 * as the previous contract by default.
 */
import { useState } from "react";
import { Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";


function addDays(iso, days) {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}


export default function RenewContractModal({ pid, source, onClose, onDone }) {
    // Default new period : starts the day after the previous end_date,
    // matches the previous duration_kind (mois → +1 mois, année → +1 an).
    const defaultStart = source?.end_date ? addDays(source.end_date, 1) : "";
    const defaultEnd = defaultStart
        ? (source?.duration_kind === "year"
            ? (() => { const d = new Date(defaultStart + "T00:00:00"); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })()
            : (() => { const d = new Date(defaultStart + "T00:00:00"); d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); })())
        : "";

    const [form, setForm] = useState({
        start_date: defaultStart,
        end_date: defaultEnd,
        duration_kind: source?.duration_kind || "month",
        monthly_minimum_bulletins: source?.monthly_minimum_bulletins ?? 30,
        primary_region: source?.primary_region || "Île-De-France",
    });
    const [saving, setSaving] = useState(false);

    const valid = form.start_date && form.end_date;

    async function submit() {
        if (!valid) {
            toast.error("Dates manquantes");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/permanents/${pid}/contracts/${source.id}/renew`, {
                ...form,
                origin: "generated",
            });
            toast.success("Contrat renouvelé — PDF prêt à télécharger");
            try { window.dispatchEvent(new Event("permanents:changed")); } catch { /* ignore */ }
            onDone?.();
        } catch (e) {
            toast.error("Renouvellement impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose} data-testid="renew-contract-modal">
            <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                            <RefreshCw className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-shariti-black">Renouveler le contrat</h2>
                            <p className="text-[12px] text-slate-500 mt-0.5">
                                Un nouveau PDF sera généré. Le contrat précédent (expiré) reste dans l'historique.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="renew-close" className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Nouvelle date de début *">
                            <input type="date" value={form.start_date}
                                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                data-testid="renew-start-date" className="input-shariti" />
                        </Field>
                        <Field label="Nouvelle date de fin *">
                            <input type="date" value={form.end_date}
                                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                data-testid="renew-end-date" className="input-shariti" />
                        </Field>
                        <Field label="Cadence">
                            <select value={form.duration_kind}
                                onChange={(e) => setForm({ ...form, duration_kind: e.target.value })}
                                className="input-shariti">
                                <option value="month">Mensuel</option>
                                <option value="year">Annuel</option>
                                <option value="custom">Personnalisé</option>
                            </select>
                        </Field>
                        <Field label="Bulletins nets min./mois">
                            <input type="number" min="0" value={form.monthly_minimum_bulletins}
                                onChange={(e) => setForm({ ...form, monthly_minimum_bulletins: parseInt(e.target.value || "30", 10) })}
                                className="input-shariti" />
                        </Field>
                        <Field label="Zone d'exercice" full>
                            <input value={form.primary_region}
                                onChange={(e) => setForm({ ...form, primary_region: e.target.value })}
                                className="input-shariti" />
                        </Field>
                    </div>
                    <div className="rounded-2xl bg-blue-50 border border-blue-200 p-3 text-[11px] text-blue-900 leading-snug">
                        La grille tarifaire du contrat précédent est reprise à l'identique. Modifiable ultérieurement.
                    </div>
                </div>

                <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-[12px] font-bold text-slate-600 hover:bg-slate-50">
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={!valid || saving}
                        data-testid="renew-submit-btn"
                        className="rounded-full bg-shariti-orange text-white px-5 py-2 text-[12px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Renouveler le contrat
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, children, full = false }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {label}
            </label>
            {children}
        </div>
    );
}
